/** Kept in this leaf module so the always-mounted navigation can link into the flow without pulling in the
 * lazily-loaded scene and everything it imports. */
export enum SourcingRecommendationView {
  Configure = 'configure',
  Recommendation = 'recommendation',
  Book = 'book',
}

export enum SourcingPurpose {
  CustomerOrInvestor = 'customerOrInvestor',
  Regulation = 'regulation',
  SupplyChainProblem = 'supplyChainProblem',
  StartingFromScratch = 'startingFromScratch',
  NotSure = 'notSure',
}

export enum SupplierVolume {
  Under250 = 'under250',
  From250To1000 = 'from250To1000',
  From1000To5000 = 'from1000To5000',
  Above5000 = 'above5000',
  NoListYet = 'noListYet',
}

export enum SourcingProcess {
  BuildingPicture = 'buildingPicture',
  SendingQuestionnaires = 'sendingQuestionnaires',
  AssessingInDepth = 'assessingInDepth',
}

export enum SourcingFeature {
  InherentRisk = 'inherentRisk',
  StandardTopicTemplates = 'standardTopicTemplates',
  CustomTemplates = 'customTemplates',
  ExpectationImprovements = 'expectationImprovements',
  ComplianceRiskMonitoring = 'complianceRiskMonitoring',
  ProductMapping = 'productMapping',
}

export enum DueDiligenceLevel {
  Screening = 'screening',
  Standard = 'standard',
  Enhanced = 'enhanced',
}

export enum OptionalScope {
  CustomContent = 'customContent',
  ProductMapping = 'productMapping',
}

export enum PlatformFeature {
  AiAutofill = 'aiAutofill',
  Api = 'api',
  Integrations = 'integrations',
  Automations = 'automations',
  AuditTracking = 'auditTracking',
  RiskMatrix = 'riskMatrix',
  AssessmentCalculation = 'assessmentCalculation',
  SpendData = 'spendData',
  UserPermissions = 'userPermissions',
  MultiLanguage = 'multiLanguage',
  EnterpriseSecurity = 'enterpriseSecurity',
}

/** What moves a supplier from the level above into this one. */
export enum LevelEntryRule {
  Risk = 'risk',
  RiskAndSpend = 'riskAndSpend',
}

export type SourcingAnswers = {
  purpose?: SourcingPurpose
  supplierVolume?: SupplierVolume
  process?: SourcingProcess
  features: SourcingFeature[]
}

/** How a supplier count derived from an answer band should be read. */
export enum SupplierBaseBound {
  /** The band is bounded, so its upper bound stands in for the real number. */
  Approximate = 'approximate',
  /** The band is open ended, so the real number is at least this. */
  AtLeast = 'atLeast',
}

export type SupplierBase = {
  count: number
  bound: SupplierBaseBound
}

export type LevelRecommendation = {
  level: DueDiligenceLevel
  coverageShare: number
  supplierCount?: SupplierBase
  /** Absent on the first level, which everyone enters by default. */
  entryRule?: LevelEntryRule
}

export type Recommendation = {
  levels: LevelRecommendation[]
  optionalScopes: OptionalScope[]
  supplierBase?: SupplierBase
}

export const emptySourcingAnswers: SourcingAnswers = { features: [] }

const LEVEL_ENTRY_RULE: Partial<Record<DueDiligenceLevel, LevelEntryRule>> = {
  [DueDiligenceLevel.Standard]: LevelEntryRule.Risk,
  [DueDiligenceLevel.Enhanced]: LevelEntryRule.RiskAndSpend,
}

// A bounded band stands in for the real number with its upper bound; the open-ended band can only give a
// floor. Taking a share of either keeps its meaning, so every level count is read the same way as its base.
const SUPPLIER_VOLUME_BASE: Partial<Record<SupplierVolume, SupplierBase>> = {
  [SupplierVolume.Under250]: { count: 250, bound: SupplierBaseBound.Approximate },
  [SupplierVolume.From250To1000]: { count: 1000, bound: SupplierBaseBound.Approximate },
  [SupplierVolume.From1000To5000]: { count: 5000, bound: SupplierBaseBound.Approximate },
  [SupplierVolume.Above5000]: { count: 5000, bound: SupplierBaseBound.AtLeast },
}

// The Recommended Due Diligence Journey screens the whole recorded base, then takes 15-25% to standard and
// 1-5% to enhanced. These are the middle of those bands.
const LEVEL_COVERAGE_SHARE: Record<DueDiligenceLevel, number> = {
  [DueDiligenceLevel.Screening]: 1,
  [DueDiligenceLevel.Standard]: 0.2,
  [DueDiligenceLevel.Enhanced]: 0.03,
}

const FEATURE_OPTIONAL_SCOPE: Partial<Record<SourcingFeature, OptionalScope>> = {
  [SourcingFeature.CustomTemplates]: OptionalScope.CustomContent,
  [SourcingFeature.ProductMapping]: OptionalScope.ProductMapping,
}

export function areAllFeaturesSelected(features: SourcingFeature[]): boolean {
  return Object.values(SourcingFeature).every(feature => features.includes(feature))
}

/** "I'm not sure, show me everything" selects every need, so the recommendation holds nothing back. */
export function toggleAllFeatures(features: SourcingFeature[]): SourcingFeature[] {
  return areAllFeaturesSelected(features) ? [] : [...Object.values(SourcingFeature)]
}

export function isSourcingAnswersComplete(answers: SourcingAnswers): boolean {
  return Boolean(answers.purpose && answers.supplierVolume && answers.process)
}

export function toggleFeature(features: SourcingFeature[], feature: SourcingFeature): SourcingFeature[] {
  return features.includes(feature)
    ? features.filter(selected => selected !== feature)
    : Object.values(SourcingFeature).filter(option => option === feature || features.includes(option))
}

function supplierBaseFor(supplierVolume: SupplierVolume | undefined): SupplierBase | undefined {
  return supplierVolume ? SUPPLIER_VOLUME_BASE[supplierVolume] : undefined
}

/** The count on its own is misleading for an open-ended band, so it is never formatted without its bound. */
export function formatSupplierCount(
  supplierBase: SupplierBase | undefined,
  formatNumber: (value: number) => string,
  translate: (id: string, values: Record<string, string>) => string,
): string | undefined {
  if (!supplierBase) return undefined

  const count = formatNumber(supplierBase.count)
  return supplierBase.bound === SupplierBaseBound.AtLeast ? translate('supplierCount.atLeast', { count }) : count
}

export function buildRecommendation(answers: SourcingAnswers): Recommendation {
  const supplierBase = supplierBaseFor(answers.supplierVolume)

  const levels = Object.values(DueDiligenceLevel).map(level => {
    const coverageShare = LEVEL_COVERAGE_SHARE[level]

    return {
      level,
      coverageShare,
      supplierCount:
        supplierBase === undefined
          ? undefined
          : { count: Math.round(supplierBase.count * coverageShare), bound: supplierBase.bound },
      entryRule: LEVEL_ENTRY_RULE[level],
    }
  })

  const optionalScopes = Object.values(SourcingFeature)
    .filter(feature => answers.features.includes(feature))
    .map(feature => FEATURE_OPTIONAL_SCOPE[feature])
    .filter((scope): scope is OptionalScope => scope !== undefined)

  return { levels, optionalScopes, supplierBase }
}

/** Sent to Calendly as the answer to the booking form's first custom question, so the call starts from what
 * the user already told us instead of asking them again. */
export function buildConsultationNote(
  answers: SourcingAnswers,
  translate: (id: string, values?: Record<string, string>) => string,
): string {
  const lines = [
    answers.purpose && translate('note.purpose', { value: translate(`purpose.${answers.purpose}`) }),
    answers.supplierVolume &&
      translate('note.supplierVolume', { value: translate(`supplierVolume.${answers.supplierVolume}`) }),
    answers.process && translate('note.process', { value: translate(`process.${answers.process}`) }),
    answers.features.length > 0 &&
      translate('note.features', {
        value: answers.features.map(feature => translate(`feature.${feature}.title`)).join(', '),
      }),
  ]

  return lines.filter(Boolean).join('\n')
}
